import {MeshTypeaheadItem} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshTypeaheadItem';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IListItem} from '@mintdata/mint-kit/api/models/List';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';

export enum InputKeyActions
{
    // Spreadsheet Input
    EnterAction = 'enterAction',
    CtrlOrAltEnterAction = 'CtrlOrAltEnterAction',
    EscAction = 'escAction',
    ShowTypeahead = 'showTypeahead',
    LeftAction = 'leftAction',
    ShiftLeftAction = 'shiftLeftAction',
    UpAction = 'upAction',
    ShiftUpAction = 'shiftUpAction',
    RightAction = 'rightAction',
    ShiftRightAction = 'shiftRightAction',
    DownAction = 'downAction',
    ShiftDownAction = 'shiftDownAction',
    TabAction = 'tabAction',
    UndoAction = 'undoAction',
    RedoAction = 'redoAction',
    SwitchCurrentCellRefKindAction = 'switchCurrentCellRefKindAction',
}

export const inputKeyMapConfig = {
    // Spreadsheet Input
    [InputKeyActions.EnterAction]: ['enter', 'shift+enter', 'mod+shift+enter', 'ctrl+shift+enter'],
    [InputKeyActions.CtrlOrAltEnterAction]: ['mod+enter', 'ctrl+enter', 'alt+enter'],
    [InputKeyActions.EscAction]: 'esc',
    [InputKeyActions.ShowTypeahead]: 'ctrl+space',
    [InputKeyActions.LeftAction]: 'left',
    [InputKeyActions.ShiftLeftAction]: 'shift+left',
    [InputKeyActions.UpAction]: 'up',
    [InputKeyActions.ShiftUpAction]: 'shift+up',
    [InputKeyActions.RightAction]: 'right',
    [InputKeyActions.ShiftRightAction]: 'shift+right',
    [InputKeyActions.DownAction]: 'down',
    [InputKeyActions.ShiftDownAction]: 'shift+down',
    [InputKeyActions.TabAction]: ['tab', 'shift+tab'],
    [InputKeyActions.UndoAction]: 'mod+z',
    [InputKeyActions.RedoAction]: 'mod+shift+z',
    [InputKeyActions.SwitchCurrentCellRefKindAction]: 'f4',
};

export class MeshInputKeyboardHandlers
{
    public static getHandlers(ctx:IWSPMeshContext)
    {
        const {main} = controllers.ui.spreadsheets;

        const editorState = main.getEditorState();
        const inputState = editorState.input;

        return {
            [InputKeyActions.SwitchCurrentCellRefKindAction]: (event:KeyboardEvent) =>
            {
                const {meshInput} = controllers.ui.spreadsheets;
                meshInput.switchCurrentCellRefKind(ctx);

                event.stopPropagation();
                event.preventDefault();
            },
            [InputKeyActions.EnterAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen, typeaheadList} = inputState;
                const {mesh, meshInput, meshSelection} = controllers.ui.spreadsheets;

                if( !isTypeaheadOpen )
                {
                    const arrayFormula = event.ctrlKey || event.metaKey;

                    // mk.debug(area.Mesh, `~~~~~~~~~~~~~~ commit value request: ${formula}`);
                    mesh.finishEditingCell(ctx, arrayFormula);

                    if( event.shiftKey )
                        meshSelection.moveActiveCellUp(ctx, true);
                    else
                        meshSelection.moveActiveCellDown(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
                else
                {
                    const selectedItem:IListItem<MeshTypeaheadItem<any>> = typeaheadList && typeaheadList.getFirstSelectedItem();

                    if( selectedItem )
                        meshInput.applyTypeaheadItem(ctx, selectedItem.getData());

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.CtrlOrAltEnterAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen, typeaheadList} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                if( !isTypeaheadOpen )
                {
                    meshInput.addTextInInputControl(ctx, DomUtils.NEW_LINE_CHAR);

                    event.stopPropagation();
                    event.preventDefault();
                }
                else
                {
                    const selectedItem:IListItem<MeshTypeaheadItem<any>> = typeaheadList && typeaheadList.getFirstSelectedItem();

                    if( selectedItem )
                        meshInput.applyTypeaheadItem(ctx, selectedItem.getData());

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.ShowTypeahead]: (event:KeyboardEvent) =>
            {
                const {meshInput} = controllers.ui.spreadsheets;
                meshInput.showFunctionTypeahead(ctx);
            },
            [InputKeyActions.EscAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                if( !isTypeaheadOpen )
                {
                    controllers.ui.spreadsheets.mesh.abortEditingCell(ctx);
                }
                else
                {
                    meshInput.hideTypeahead();
                }

                event.stopPropagation();
                event.preventDefault();
            },
            [InputKeyActions.LeftAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRef(ctx, -1, 0)
                    : meshInput.selectNewCellRef(ctx, -1, 0);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.ShiftLeftAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRefRange(ctx, -1, 0)
                    : meshInput.selectNewCellRefRange(ctx, -1, 0);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.UpAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen, typeaheadList} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRef(ctx, 0, -1)
                    : meshInput.selectNewCellRef(ctx, 0, -1);

                if( isTypeaheadOpen )
                {
                    typeaheadList.selectPreviousItem();

                    event.stopPropagation();
                    event.preventDefault();
                }
                else if( tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.ShiftUpAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRefRange(ctx, 0, -1)
                    : meshInput.selectNewCellRefRange(ctx, 0, -1);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.RightAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRef(ctx, 1, 0)
                    : meshInput.selectNewCellRef(ctx, 1, 0);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.ShiftRightAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRefRange(ctx, 1, 0)
                    : meshInput.selectNewCellRefRange(ctx, 1, 0);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.DownAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen, typeaheadList} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRef(ctx, 0, 1)
                    : meshInput.selectNewCellRef(ctx, 0, 1);

                if( isTypeaheadOpen )
                {
                    typeaheadList.selectNextItem();

                    event.stopPropagation();
                    event.preventDefault();
                }
                else if( tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.ShiftDownAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen} = inputState;
                const {meshInput} = controllers.ui.spreadsheets;

                const tryToSelectCell:boolean = (editorState.editSheetId !== ctx.sheetId)
                    ? meshInput.selectNewExternalCellRefRange(ctx, 0, 1)
                    : meshInput.selectNewCellRefRange(ctx, 0, 1);

                if( !isTypeaheadOpen && tryToSelectCell )
                {
                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [InputKeyActions.TabAction]: (event:KeyboardEvent) =>
            {
                const {isTypeaheadOpen, typeaheadList} = inputState;
                const {meshInput, meshSelection} = controllers.ui.spreadsheets;

                if( isTypeaheadOpen )
                {
                    if( !event.shiftKey )
                    {
                        const selectedItem:IListItem<MeshTypeaheadItem<any>> = typeaheadList && typeaheadList.getFirstSelectedItem();

                        if( selectedItem )
                            meshInput.applyTypeaheadItem(ctx, selectedItem.getData());
                    }
                }
                else
                {
                    if( controllers.ui.spreadsheets.main.isEditMode )
                    {
                        controllers.ui.spreadsheets.mesh.commitEditingCellIfValid(ctx);
                    }

                    if( event.shiftKey )
                        meshSelection.moveActiveCellLeft(ctx);
                    else
                        meshSelection.moveActiveCellRight(ctx);
                }

                event.stopPropagation();
                event.preventDefault();
            },
            [InputKeyActions.UndoAction]: (event:KeyboardEvent) =>
            {
                event.stopPropagation();
                event.preventDefault();
            },
            [InputKeyActions.RedoAction]: (event:KeyboardEvent) =>
            {
                event.stopPropagation();
                event.preventDefault();
            },
        };
    }
}
